<script setup>
import GroupedButtons from "../GroupedButtons.vue";
import PasswordField from "../PasswordField.vue";
import PhotoField from "../PhotoField.vue";
import {ref, watch} from "vue";
import {useForm} from "laravel-precognition-vue";

const {value, user} = defineProps(['value', 'user']);
const emit = defineEmits(['step', 'change']);

const loading = ref( true );
const is_auth = ref( value );
const login_form = useForm('post', '/api/v1/auth/login', {
    email: '',
    password: '',
    remember_me: '',
});
const register_form = useForm('post', '/api/v1/auth/register', {
    gender: 'man',
    age_ranch: 'adult',
    firstname: '',
    lastname: '',
    email: '',
    phone_number: '',
    password: '',
    password_confirmation: '',
    picture: null,
    captcha: '',
});
const selected_gender = ref( 'man' );
const selected_age_ranch = ref( 'adult' );
const form_type = ref('register');

const choicesLabels = {
    man: {'adult': 'un adulte', 'child': 'un enfant'},
    woman: {'adult': 'une adulte', 'child': 'une enfant'},
};
const choices = ref( choicesLabels['man'] );

watch( selected_gender, value => choices.value = choicesLabels[ value ] );
watch( () => value, value => is_auth.value = value );

function selectAgeRanch(value) {
    selected_age_ranch.value = value;
    register_form.age_ranch = value;
}

function selectSex(value) {
    selected_gender.value = value;
    register_form.gender = value;
}

async function submitLogin() {
    loading.value = true;

    try {
        const response = await login_form.submit();

        if ( response.data.status ) {
            emit('change', true );
            login_form.reset();
        }
    } catch (e) {
        console.error( e.message );
    } finally {
        loading.value = false;
    }
}

async function submitRegister() {
    loading.value = true;

    try {
        if ( grecaptcha !== undefined ) {
            const recaptcha = await grecaptcha.execute( window.recaptcha_site_key , { action: 'REGISTER' } );
            register_form.captcha = recaptcha;
        }

        const response = await register_form.submit({
            preserveScroll: true,
            forceFormData: true,
            headers: {
                'Content-Type': 'multipart/form-data'
            },
        });

        if ( response.data.status ) {
            emit('change', true );
            register_form.reset();
        }
    } catch (e) {
        console.error( e.message );
    } finally {
        loading.value = false;
    }
}
</script>

<template>
    <div class="flex flex-col">
        <div class="flex flex-col lg:flex-row mb-10">
            <div class="mb-10 lg:mb-0">
                <button type="button" @click="emit('step', 'previous')" title="Retour" aria-label="Retour"
                        class="button-invert px-5 py-2">
                    <i class="fa-solid fa-angle-left | mr-2"></i> Retour
                </button>
            </div>
            <h2 class="text-white text-4xl text-center lg:w-10/12">Mon compte</h2>
        </div>

        <div class="flex flex-col lg:flex-row gap-x-10">
            <div class="w-full lg:w-2/3">
                <div class="flex flex-col mb-10" v-if="!is_auth">
                    <grouped-buttons class="w-full lg:w-2/3 mx-auto" @change="value => form_type = value"
                                     :value="'register'" :choices="{'register': 'Je souhaite m\'inscrire', 'login': 'J\'ai déjà un compte'}"></grouped-buttons>
                </div>

                <div class="flex flex-col mx-auto border rounded-3xl mb-10" v-if="!is_auth && form_type === 'register'">
                    <div class="border-b p-5 sm:p-10">
                        <h3 class="text-white text-xl font-bold mb-5">Mes informations personnelles</h3>

                        <div class="flex flex-col sm:flex-row mb-10">
                            <label class="text-white flex flex-col justify-center mb-5 sm:mb-0"><span class="important">Je suis</span></label>
                            <div class="flex-1 sm:ml-5 lg:ml-10">
                                <grouped-buttons class="w-full" @input="selectSex"
                                                 :value="'man'" :choices="{'man': 'un homme', 'woman': 'une femme'}"></grouped-buttons>
                            </div>
                        </div>

                        <div class="flex flex-col sm:flex-row mb-10">
                            <label class="text-white flex flex-col justify-center mb-5 sm:mb-0"><span class="important">Je suis</span></label>
                            <div class="flex-1 sm:ml-5 lg:ml-10">
                                <grouped-buttons class="w-full" @input="selectAgeRanch"
                                                 :value="'adult'" :choices="choices"></grouped-buttons>
                            </div>
                        </div>

                        <div class="flex flex-col lg:flex-row gap-y-5 gap-x-10 mb-10">
                            <div class="w-full lg:w-1/2">
                                <label class="important | text-white block mb-2" for="input_firstname">Nom</label>
                                <input type="text" id="input_firstname" class="w-full py-1 px-2"
                                       v-model="register_form.firstname">
                                <div class="text-red-500 mt-2.5" v-if="register_form.invalid('firstname')">
                                    {{ register_form.errors.firstname }}
                                </div>
                            </div>

                            <div class="w-full lg:w-1/2">
                                <label class="important | text-white block mb-2" for="input_lastname">Prénom</label>
                                <input type="text" id="input_lastname" class="w-full py-1 px-2"
                                       v-model="register_form.lastname">
                                <div class="text-red-500 mt-2.5" v-if="register_form.invalid('lastname')">
                                    {{ register_form.errors.lastname }}
                                </div>
                            </div>
                        </div>

                        <div class="flex flex-col lg:flex-row gap-y-5 gap-x-10 mb-10">
                            <div class="w-full lg:w-1/2">
                                <label class="important | text-white block mb-2" for="input_phone">Numéro de téléphone</label>
                                <input type="text" id="input_phone" class="w-full py-1 px-2"
                                       v-model="register_form.phone_number">
                                <div class="text-red-500 mt-2.5" v-if="register_form.invalid('phone_number')">
                                    {{ register_form.errors.phone_number }}
                                </div>
                            </div>
                            <div class="w-full lg:w-1/2 text-white flex flex-col justify-end">
                                En cas de personne mineure, veuillez indiquer le numéro de téléphone du responsable légal.
                            </div>
                        </div>

                        <div class="flex flex-row lg:gap-x-10">
                            <div class="w-full lg:w-2/3">
                                <label class="text-white block mb-2" for="input_picture">Photo de profil</label>
                                <photo-field name="picture" image="dist/img/profile-picture.jpg" v-model="register_form.picture">
                                    Max 100 ko<br/>Max 500*500px<br/>Format .jpg, .jpeg ou .png
                                </photo-field>
                            </div>
                            <div class="w-0 lg:w-1/3"></div>
                        </div>
                    </div>

                    <div class="p-5 sm:p-10">
                        <h3 class="text-white text-xl font-bold mb-5">Mes informations de connexion</h3>

                        <div class="flex flex-col lg:flex-row gap-x-10 mb-5 lg:mb-10">
                            <div class="w-full lg:w-1/2">
                                <label class="important | text-white block mb-2" for="input_email">Adresse email</label>
                                <input type="email" id="input_email" class="w-full py-1 px-2"
                                       v-model="register_form.email">
                                <div class="text-red-500 mt-2.5" v-if="register_form.invalid('email')">
                                    {{ register_form.errors.email }}
                                </div>
                            </div>
                            <div class="w-0 lg:w-1/2"></div>
                        </div>

                        <div class="flex flex-col lg:flex-row gap-y-5 gap-x-10 mb-10">
                            <div class="w-full lg:w-1/2">
                                <label class="important | text-white block mb-2" for="input_password">Mot de passe</label>
                                <password-field name="password" id="input_password" v-model="register_form.password"></password-field>
                                <div class="text-red-500 mt-2.5" v-if="register_form.invalid('password')">
                                    {{ register_form.errors.password }}
                                </div>
                            </div>

                            <div class="w-full lg:w-1/2">
                                <label class="important | text-white block mb-2" for="input_password_confirmation">Confirmer</label>
                                <password-field name="password" id="input_password_confirmation" v-model="register_form.password_confirmation"></password-field>
                                <div class="text-red-500 mt-2.5" v-if="register_form.invalid('password_confirmation')">
                                    {{ register_form.errors.password_confirmation }}
                                </div>
                            </div>
                        </div>

                        <div class="flex flex-row gap-x-10">
                            <div class="w-full lg:w-1/2 mx-auto px-5">
                                <button type="button" :disabled="register_form.processing" @click="submitRegister" title="Créer mon compte" aria-label="Créer mon compte"
                                        class="button | w-full py-2 disabled:opacity-50 disabled:cursor-not-allowed">
                                    <span v-if="!register_form.processing">Créer mon compte</span>
                                    <i v-if="register_form.processing" class="fa-solid fa-spinner fa-spin"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="flex flex-col mx-auto p-5 sm:p-10 border rounded-3xl mb-10" v-if="!is_auth && form_type === 'login'">
                    <h3 class="text-white text-xl font-bold mb-5">Connexion à mon compte</h3>

                    <div class="w-full lg:w-2/3 xl:w-1/2 mx-auto">
                        <div class="mb-5">
                            <label for="input_email" class="important | block text-white mb-2">Email</label>
                            <input type="email" name="email" id="input_email" class="w-full py-1 px-2" v-model="login_form.email">
                            <div class="text-red-500 mt-2.5" v-if="login_form.invalid('email')">
                                {{ login_form.errors.email }}
                            </div>
                        </div>

                        <div class="mb-5">
                            <label for="input_password" class="important | block text-white mb-2">Mot de passe</label>
                            <password-field name="password" id="input_password" v-model="login_form.password"></password-field>
                            <div class="text-red-500 mt-2.5" v-if="login_form.invalid('password')">
                                {{ login_form.errors.password }}
                            </div>
                        </div>

                        <div class="mb-5">
                            <input type="checkbox" name="remember_me" id="input_remember_me" class="relative top-1 w-5 h-5 mr-2 cursor-pointer" v-model="login_form.remember_me">
                            <label for="input_remember_me" class="text-white mb-2 mr-2 cursor-pointer">Se souvenir de moi ?</label>
                        </div>

                        <div class="mb-5">
                            <button type="button" :disabled="login_form.processing" @click="submitLogin"
                                    class="button | w-full py-2 disabled:opacity-50 disabled:cursor-not-allowed">
                                <span v-if="!login_form.processing">Me connecter</span>
                                <i v-if="login_form.processing" class="fa-solid fa-spinner fa-spin"></i>
                            </button>
                        </div>

                        <p class="text-white">
                            Vous avez oublié votre mot de passe ? <br/>
                            <a href="/password/email" class="link" target="_blank">Réinitialiser mon mot de passe</a>
                        </p>
                    </div>
                </div>

                <div class="flex flex-col mx-auto p-5 sm:p-10 border rounded-3xl mb-10" v-if="is_auth">
                    <h3 class="text-white text-xl font-bold mb-5">Mes informations</h3>

                    <div class="mb-2 text-white">
                        <span class="font-bold">Votre nom et prénom:</span> {{ user.firstname }} {{ user.lastname }}
                    </div>

                    <div class="mb-2 text-white">
                        <span class="font-bold">Votre adresse email:</span> {{ user.email }}
                    </div>

                    <div class="mb-5 text-white">
                        <span class="font-bold">Votre numéro de téléphone:</span> {{ user.phone_number }}
                    </div>

                    <div class="text-white">
                        Ces informations ne sont plus à jours ?<br/>Vous pouvez les modifier dans <a href="/mon-profil" class="link" target="_blank">votre profil</a> à tout moment.
                    </div>
                </div>
            </div>

            <div class="w-full lg:w-1/3 mb-10 relative">
                <slot></slot>
            </div>
        </div>

        <div class="text-center">
            <button class="button px-5 py-3" type="button" @click="emit('step', 'confirmation')" :disabled="!is_auth">Suivant <i class="fa-solid fa-angle-right | ml-2"></i></button>
        </div>
    </div>
</template>